if (!customElements.get('lgd-subscription-purchase-options')) {
  const LOOP_SUBSCRIPTIONS_APP_ID = '5284869';

  customElements.define(
    'lgd-subscription-purchase-options',
    class SubscriptionPurchaseOptions extends HTMLElement {
      constructor() {
        super();

        this.purchaseType = 'subscription';
        this.selectedPlanId = '';
        this.currentVariant = null;
        this.availablePlans = [];
        this.buttonObserver = null;
        this.formObserver = null;
        this.nativeObserver = null;
        this.formSyncTimer = null;
        this.nativeSyncTimer = null;
      }

      connectedCallback() {
        this.form = this.closest('form');
        this.productInfo = this.closest('product-info');
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.planInput = this.querySelector('[data-subscription-selling-plan]');
        this.subscriptionRadio = this.querySelector('[data-subscription-radio]');
        this.oneTimeRadio = this.querySelector('[data-onetime-radio]');
        this.planSelect = this.querySelector('[data-subscription-plan-select]');
        this.quantityInput = this.form?.querySelector('input[name="quantity"]');
        this.submitButton = this.form?.querySelector('[type="submit"][name="add"]');
        this.submitButtonLabel = this.submitButton?.querySelector('[data-atc-label]');

        try {
          this.productData = JSON.parse(this.querySelector('[data-subscription-product-json]')?.textContent || '{}');
        } catch (error) {
          console.error('Unable to initialize subscription purchase options.', error);
          return;
        }

        if (!this.form || !this.variantInput || !this.productData?.variants?.length) return;

        this.subscriptionRadio?.addEventListener('change', () => this.onPurchaseTypeChange('subscription'));
        this.oneTimeRadio?.addEventListener('change', () => this.onPurchaseTypeChange('one-time'));
        this.planSelect?.addEventListener('change', () => this.onPlanChange());
        this.variantInput.addEventListener('change', () => this.syncVariant());
        this.quantityInput?.addEventListener('input', () => this.updateAddToCartTotal());
        this.quantityInput?.addEventListener('change', () => this.updateAddToCartTotal());
        this.form.addEventListener('submit', () => this.syncFormInputs(), true);
        this.querySelector('[data-subscription-details]')?.addEventListener('click', () => this.openSubscriptionDetails());

        if (this.submitButtonLabel) {
          this.buttonObserver = new MutationObserver(() => this.updateAddToCartTotal());
          this.buttonObserver.observe(this.submitButtonLabel, { childList: true, characterData: true, subtree: true });
        }

        this.formObserver = new MutationObserver((mutations) => {
          const sellingPlanInputsChanged = mutations.some((mutation) =>
            [...mutation.addedNodes, ...mutation.removedNodes].some(
              (node) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.matches?.('input[name="selling_plan"]') || node.querySelector?.('input[name="selling_plan"]')),
            ),
          );

          if (sellingPlanInputsChanged) this.scheduleFormInputSync();
        });
        this.formObserver.observe(this.form, { childList: true, subtree: true });

        if (this.productInfo) {
          this.nativeObserver = new MutationObserver((mutations) => {
            const hasLoopWidgetAddition = mutations.some((mutation) =>
              Array.from(mutation.addedNodes).some(
                (node) =>
                  node.nodeType === Node.ELEMENT_NODE &&
                  (node.matches?.('.loop-widget-container, .loop-subscription-container') ||
                    node.querySelector?.('.loop-widget-container, .loop-subscription-container')),
              ),
            );

            if (hasLoopWidgetAddition) {
              if (this.hidden) {
                this.syncVariant();
              } else {
                this.scheduleNativeSync();
              }
            }
          });
          this.nativeObserver.observe(this.productInfo, { childList: true, subtree: true });
        }

        this.syncVariant(true);
      }

      disconnectedCallback() {
        this.buttonObserver?.disconnect();
        this.formObserver?.disconnect();
        this.nativeObserver?.disconnect();
        window.clearTimeout(this.formSyncTimer);
        window.clearTimeout(this.nativeSyncTimer);
        this.disableCustomPlanInput();
        this.restoreNativeInputs();
        this.productInfo?.removeAttribute('data-lgd-subscription-ui-active');
      }

      getVariant() {
        const variantId = String(this.variantInput?.value || '');
        return this.productData.variants.find((variant) => String(variant.id) === variantId) || null;
      }

      getPlansForVariant(variant) {
        const allocations = variant?.selling_plan_allocations || [];
        if (!allocations.length) return [];

        const groups = this.productData.selling_plan_groups || [];
        const planIndex = new Map();

        groups.forEach((group) => {
          (group.selling_plans || []).forEach((plan) => {
            planIndex.set(String(plan.id), { group, plan });
          });
        });

        const normalized = allocations
          .map((allocation) => {
            const planId = String(allocation.selling_plan_id || allocation.selling_plan?.id || '');
            const indexed = planIndex.get(planId);
            const plan = indexed?.plan || allocation.selling_plan || {};
            const group = indexed?.group || {};
            const option = plan.options?.[0] || {};
            const groupOption = group.options?.[0] || {};
            const label = option.name || groupOption.name || 'Deliver every';
            const value = option.value || plan.name || '';
            const price = Number(allocation.price ?? allocation.per_delivery_price);
            const allocationCompareAtPrice = Number(allocation.compare_at_price);
            const compareAtPrice =
              Number.isFinite(allocationCompareAtPrice) && allocationCompareAtPrice > 0
                ? allocationCompareAtPrice
                : null;

            if (!planId || !Number.isFinite(price)) return null;

            return {
              id: planId,
              price,
              compareAtPrice,
              label,
              value,
              description: plan.description || '',
              appId: String(group.app_id || ''),
              groupId: String(allocation.selling_plan_group_id || group.id || ''),
            };
          })
          .filter(Boolean);

        if (!normalized.length) return [];

        const loopGroupIds = this.getLoopGroupIds(variant, groups, normalized);
        if (loopGroupIds.length) {
          const selectedGroupId = this.getPreferredLoopGroupId(variant, loopGroupIds);
          return normalized.filter((plan) => plan.groupId === selectedGroupId);
        }

        const availableGroupIds = [...new Set(normalized.map((plan) => plan.groupId).filter(Boolean))];
        return availableGroupIds.length === 1 ? normalized : [];
      }

      getLoopGroupIds(variant, groups, plans) {
        const availableGroupIds = new Set(plans.map((plan) => plan.groupId).filter(Boolean));
        const productId = String(this.productData?.id || '');
        const loopProps = window.loopProps?.[productId];
        const variantId = String(variant?.id || '');

        const nativeGroupIds = Array.from(
          this.productInfo?.querySelectorAll('.loop-subscription-group-radio[data-id]') || [],
        )
          .filter((radio) => !radio.dataset.variantId || String(radio.dataset.variantId) === variantId)
          .map((radio) => String(radio.dataset.id || ''));

        const loopPropsGroupIds = (loopProps?.product?.selling_plan_groups || []).map((group) => String(group.id || ''));
        const knownAppGroupIds = groups
          .filter((group) => String(group.app_id || '') === LOOP_SUBSCRIPTIONS_APP_ID)
          .map((group) => String(group.id || ''));

        return [...new Set([...nativeGroupIds, ...loopPropsGroupIds, ...knownAppGroupIds])].filter((groupId) =>
          availableGroupIds.has(groupId),
        );
      }

      getPreferredLoopGroupId(variant, loopGroupIds) {
        const allowedGroupIds = new Set(loopGroupIds);
        const productId = String(this.productData?.id || '');
        const loopProps = window.loopProps?.[productId];
        const variantId = String(variant?.id || '');
        const nativeRadios = Array.from(
          this.productInfo?.querySelectorAll('.loop-subscription-group-radio[data-id]') || [],
        ).filter((radio) => !radio.dataset.variantId || String(radio.dataset.variantId) === variantId);
        const checkedGroupId = String(nativeRadios.find((radio) => radio.checked)?.dataset.id || '');
        const preferredGroupIds = [
          String(loopProps?.sellingPlanGroupId || ''),
          checkedGroupId,
          ...(loopProps?.nonBundleSPGS || []).map(String),
          ...nativeRadios.map((radio) => String(radio.dataset.id || '')),
          ...loopGroupIds,
        ];

        return preferredGroupIds.find((groupId) => allowedGroupIds.has(groupId)) || loopGroupIds[0];
      }

      syncVariant(initial = false) {
        const variant = this.getVariant();
        const plans = this.getPlansForVariant(variant);

        if (!variant || !plans.length) {
          this.deactivate();
          return;
        }

        const previousPackCount = this.getVariantPackCount(this.currentVariant);
        const nextPackCount = this.getVariantPackCount(variant);
        const selectedPlanIsAvailable = plans.some((plan) => plan.id === String(this.selectedPlanId));
        const preferredPlan = this.getPreferredPlanForVariant(variant, plans);
        const packChanged =
          previousPackCount !== null && nextPackCount !== null && previousPackCount !== nextPackCount;

        this.currentVariant = variant;
        this.availablePlans = plans;

        if (!selectedPlanIsAvailable || initial || packChanged) {
          this.selectedPlanId = preferredPlan?.id || plans[0].id;
        }

        if (this.shouldHideOneTime()) this.purchaseType = 'subscription';

        this.renderPlanOptions();
        this.renderState();
        this.hidden = false;
        this.productInfo?.setAttribute('data-lgd-subscription-ui-active', 'true');
        this.syncFormInputs();

        this.updateAddToCartTotal();
        this.scheduleNativeSync();
        this.dispatchPlanChange();
      }

      deactivate() {
        this.hidden = true;
        this.currentVariant = null;
        this.availablePlans = [];
        this.selectedPlanId = '';
        this.disableCustomPlanInput();
        this.productInfo?.removeAttribute('data-lgd-subscription-ui-active');
        this.restoreNativeInputs();
      }

      renderPlanOptions() {
        if (!this.planSelect) return;

        this.planSelect.replaceChildren();

        this.availablePlans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = plan.id;
          option.textContent = this.getPlanDisplayValue(plan);
          option.selected = plan.id === String(this.selectedPlanId);
          this.planSelect.appendChild(option);
        });

        const selectedPlan = this.getSelectedPlan();
        const label = selectedPlan?.label || 'Deliver every';
        const labelElement = this.querySelector('[data-subscription-frequency-label]');
        if (labelElement) labelElement.textContent = label;
      }

      getPlanDisplayValue(plan) {
        const value = String(plan.value || '').trim();
        if (String(plan.label).trim().toLowerCase() === 'frequency' && !/^deliver every\b/i.test(value)) {
          return `Deliver every ${value}`;
        }
        return value;
      }

      getVariantPackCount(variant) {
        if (!variant) return null;

        const searchableText = [variant.title, ...(variant.options || []), variant.option1, variant.option2, variant.option3]
          .filter(Boolean)
          .join(' ');
        const match = searchableText.match(/\b(\d+)\s*[- ]*(?:pack|pk)\b/i);

        return match ? Number.parseInt(match[1], 10) : null;
      }

      getPreferredPlanForVariant(variant, plans) {
        const packCount = this.getVariantPackCount(variant);
        if (!packCount) return null;

        const expectedDays = packCount * 30;
        const cadencePattern = new RegExp(`\\b${expectedDays}\\s*days?\\b`, 'i');
        return plans.find((plan) => cadencePattern.test(`${plan.value} ${plan.label}`)) || null;
      }

      renderState() {
        const selectedPlan = this.getSelectedPlan();
        if (!selectedPlan || !this.currentVariant) return;

        const retailPrice = this.getSubscriptionCompareAtPrice(selectedPlan);
        const oneTimePrice = this.getOneTimePrice();
        const subscriptionPrice = selectedPlan.price;
        const discount = retailPrice > 0 ? Math.max(0, Math.round((1 - subscriptionPrice / retailPrice) * 100)) : 0;

        this.dataset.purchaseType = this.purchaseType;
        this.subscriptionRadio.checked = this.purchaseType === 'subscription';
        this.oneTimeRadio.checked = this.purchaseType === 'one-time';

        const oneTimeOption = this.querySelector('[data-subscription-onetime]');
        const hideOneTime = this.shouldHideOneTime();
        oneTimeOption.hidden = hideOneTime;
        this.oneTimeRadio.disabled = hideOneTime;

        this.setText('[data-subscription-discount]', discount);
        this.setText('[data-subscription-retail-price]', this.formatMoney(retailPrice));
        this.setText('[data-subscription-price]', this.formatMoney(subscriptionPrice));
        this.setText('[data-onetime-price]', this.formatMoney(oneTimePrice));
        this.renderOneTimeComparePrice(oneTimePrice);

        const planDescription = this.querySelector('[data-subscription-plan-description]');
        if (planDescription) {
          const isAcvCompareAtDiscount =
            this.dataset.useLoopDiscountForBanner === 'true';

          const defaultDescription = isAcvCompareAtDiscount
            ? 'Your subscription renews automatically. You can skip, change or cancel anytime.'
            : discount > 0
              ? `Subscribers save ${discount}% on your first order and ${discount}% on every subsequent order.`
              : 'Your subscription renews automatically. You can skip, change or cancel anytime.';
          planDescription.textContent =
            String(selectedPlan.description || '').trim() || defaultDescription;

          planDescription.hidden = false;
        }

        const detailsContent = this.querySelector('[data-subscription-details-content]');
        if (detailsContent) {
          detailsContent.textContent =
            selectedPlan.description ||
            'Your subscription renews automatically. You can skip, change or cancel anytime.';
        }
      }

      onPurchaseTypeChange(purchaseType) {
        this.purchaseType = purchaseType;
        this.renderState();
        this.syncFormInputs();
        this.syncNativeWidget();
        this.updateAddToCartTotal();
        this.dispatchPlanChange();
      }

      onPlanChange() {
        this.selectedPlanId = String(this.planSelect.value);
        this.renderState();
        this.syncFormInputs();
        this.syncNativeWidget();
        this.updateAddToCartTotal();
        this.dispatchPlanChange();
      }

      getSelectedPlan() {
        return this.availablePlans.find((plan) => plan.id === String(this.selectedPlanId)) || this.availablePlans[0];
      }

      getRetailPrice(plan = this.getSelectedPlan()) {
        const allocationCompareAtPrice = Number(plan?.compareAtPrice);
        if (Number.isFinite(allocationCompareAtPrice) && allocationCompareAtPrice > 0) {
          return allocationCompareAtPrice;
        }

        const variantPrice = Number(this.currentVariant?.price);
        return Number.isFinite(variantPrice) ? variantPrice : 0;
      }

      getSubscriptionCompareAtPrice(plan = this.getSelectedPlan()) {
        if (this.dataset.useLoopDiscountForBanner === 'true') {
          const variantCompareAtPrice = Number(this.currentVariant?.compare_at_price);
          const subscriptionPrice = Number(plan?.price);

          if (
            Number.isFinite(variantCompareAtPrice) &&
            variantCompareAtPrice > 0 &&
            Number.isFinite(subscriptionPrice) &&
            variantCompareAtPrice > subscriptionPrice
          ) {
            return variantCompareAtPrice;
          }
        }

        return this.getRetailPrice(plan);
      }

      getCustomDiscountPercent() {
        if (!this.currentVariant) return 0;

        const metaEl = document.getElementById(`variant-meta-${this.productData?.id}`);
        if (!metaEl) return 0;

        try {
          const meta = JSON.parse(metaEl.textContent);
          const discount = Number(meta[this.currentVariant.id]?.custom_discount);
          return Number.isFinite(discount) ? discount : 0;
        } catch (error) {
          return 0;
        }
      }

      getOneTimePrice() {
        const variantPrice = Number(this.currentVariant?.price);
        if (!Number.isFinite(variantPrice)) return 0;

        const discount = this.getCustomDiscountPercent();
        if (discount > 0 && discount < 100) {
          return Math.round(variantPrice * (1 - discount / 100));
        }

        return variantPrice;
      }

      renderOneTimeComparePrice(oneTimePrice) {
        const compareEl = this.querySelector('[data-onetime-compare-price]');
        if (!compareEl) return;

        const compareAtPrice = Number(this.currentVariant?.compare_at_price);
        const hasCompareAt = Number.isFinite(compareAtPrice) && compareAtPrice > oneTimePrice;

        compareEl.hidden = !hasCompareAt;
        compareEl.textContent = hasCompareAt ? this.formatMoney(compareAtPrice) : '';
      }

      shouldHideOneTime() {
        return this.dataset.requiresSellingPlan === 'true' || this.dataset.hideOnetime === 'true';
      }

      syncFormInputs() {
        if (!this.form || this.hidden) return;

        const planInput = this.ensurePlanInput();

        this.form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
          if (input === planInput) return;
          if (!input.disabled) input.dataset.lgdDisabledBySubscriptionUi = 'true';
          input.disabled = true;
        });

        if (this.purchaseType === 'subscription' && this.getSelectedPlan()) {
          planInput.value = this.getSelectedPlan().id;
          planInput.disabled = false;
        } else {
          planInput.value = '';
          planInput.disabled = true;
        }
      }

      ensurePlanInput() {
        if (!this.planInput) {
          this.planInput = document.createElement('input');
          this.planInput.type = 'hidden';
          this.planInput.name = 'selling_plan';
          this.planInput.dataset.subscriptionSellingPlan = '';
        }

        if (!this.contains(this.planInput)) this.prepend(this.planInput);
        return this.planInput;
      }

      disableCustomPlanInput() {
        if (!this.planInput) return;
        this.planInput.value = '';
        this.planInput.disabled = true;
      }

      restoreNativeInputs() {
        this.form?.querySelectorAll('[data-lgd-disabled-by-subscription-ui="true"]').forEach((input) => {
          input.disabled = false;
          delete input.dataset.lgdDisabledBySubscriptionUi;
        });
      }

      scheduleFormInputSync() {
        if (this.hidden) return;
        window.clearTimeout(this.formSyncTimer);
        this.formSyncTimer = window.setTimeout(() => this.syncFormInputs(), 0);
      }

      scheduleNativeSync() {
        window.clearTimeout(this.nativeSyncTimer);
        this.nativeSyncTimer = window.setTimeout(() => this.syncNativeWidget(), 50);
      }

      syncNativeWidget() {
        if (!this.productInfo || this.hidden) return;

        const nativeOptions = Array.from(
          this.productInfo.querySelectorAll(
            '.loop-widget-purchase-option, .loop-one-time-purchase-option, .loop-subscription-group',
          ),
        ).filter((option) => !this.contains(option));

        if (nativeOptions.length) {
          const selectedPlan = this.getSelectedPlan();
          const desiredOption =
            this.purchaseType === 'one-time'
              ? nativeOptions.find(
                  (option) =>
                    option.hasAttribute('data-loop-widget-onetime-purchase-option') ||
                    option.classList.contains('loop-one-time-purchase-option'),
                )
              : nativeOptions.find((option) => {
                  const groupId = String(
                    option.querySelector('.loop-subscription-group-radio[data-id]')?.dataset.id || '',
                  );
                  return groupId && groupId === selectedPlan?.groupId;
                }) ||
                nativeOptions.find(
                  (option) =>
                    !option.hasAttribute('data-loop-widget-onetime-purchase-option') &&
                    !option.classList.contains('loop-one-time-purchase-option'),
                );

          if (desiredOption && !desiredOption.classList.contains('loop-widget-purchase-option-selected')) {
            const radio = desiredOption.querySelector('input[type="radio"], .loop-widget-purchase-option-radio');
            (radio || desiredOption).click();
          }
        }

        if (this.purchaseType === 'subscription') {
          const nativeSelects = Array.from(
            this.productInfo.querySelectorAll('.loop-widget-sp-selector, .loop-selling-plan-selector'),
          ).filter((select) => !this.contains(select));

          nativeSelects.forEach((select) => {
            if (Array.from(select.options || []).some((option) => String(option.value) === String(this.selectedPlanId))) {
              select.value = String(this.selectedPlanId);
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        this.syncFormInputs();
      }

      dispatchPlanChange() {
        this.dispatchEvent(
          new CustomEvent('selling-plan:change', {
            bubbles: true,
            detail: {
              source: 'lgd-subscription-buy-box',
              planType: this.purchaseType,
              sellingPlanId: this.purchaseType === 'subscription' ? this.getSelectedPlan()?.id || '' : '',
            },
          }),
        );
      }

      updateAddToCartTotal() {
        if (!this.submitButton || !this.submitButtonLabel || !this.currentVariant || this.hidden) return;
        if (this.currentVariant.available === false) return;
        if (this.submitButton.disabled) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true' && !this.submitButton.classList.contains('loading')) return;

        const quantity = Math.max(1, Number.parseInt(this.quantityInput?.value || '1', 10) || 1);
        const selectedPlan = this.getSelectedPlan();
        const unitPrice =
          this.purchaseType === 'subscription' && selectedPlan ? selectedPlan.price : this.getOneTimePrice();
        const buttonText = `Add to cart - ${this.formatMoney(unitPrice * quantity)}`;

        if (this.submitButtonLabel.textContent.trim() !== buttonText) {
          this.submitButtonLabel.textContent = buttonText;
        }
      }

      formatMoney(cents) {
        const amount = Number(cents) / 100;
        const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
        const locale = this.dataset.locale || document.documentElement.lang || 'en-US';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          return `$${amount.toFixed(2)}`;
        }
      }

      setText(selector, value) {
        const element = this.querySelector(selector);
        if (element) element.textContent = String(value);
      }

      openSubscriptionDetails() {
        const nativeDetails = Array.from(this.productInfo?.querySelectorAll('a, button, summary') || []).find(
          (element) =>
            !this.contains(element) && element.textContent.trim().toLowerCase() === 'subscription details',
        );

        if (nativeDetails) {
          nativeDetails.click();
          return;
        }

        const button = this.querySelector('[data-subscription-details]');
        const content = this.querySelector('[data-subscription-details-content]');
        const shouldOpen = content.hidden;
        content.hidden = !shouldOpen;
        button.setAttribute('aria-expanded', String(shouldOpen));
      }
    },
  );
}
